import type { ComponentProps, ReactElement } from 'react';
import { Check, ChevronDown, ChevronUp } from 'lucide-react';
import { Select as SelectPrimitive } from 'radix-ui';

import { cn } from '../utils/tw-merge.util';

export interface SelectOption<T extends string = string> {
  label: string;
  value: T;
}

export const Select = SelectPrimitive.Root;
export const SelectGroup = SelectPrimitive.Group;
export const SelectValue = SelectPrimitive.Value;

type SelectTriggerProps = ComponentProps<typeof SelectPrimitive.Trigger>;
type SelectScrollProps = ComponentProps<typeof SelectPrimitive.ScrollUpButton>;
type SelectContentProps = ComponentProps<typeof SelectPrimitive.Content>;
type SelectLabelProps = ComponentProps<typeof SelectPrimitive.Label>;
type SelectItemProps = ComponentProps<typeof SelectPrimitive.Item> & {
  indicatorSide?: 'left' | 'right';
};
type SelectSeparatorProps = ComponentProps<typeof SelectPrimitive.Separator>;

export function SelectTrigger({ className, children, ...props }: SelectTriggerProps): ReactElement {
  return (
    <SelectPrimitive.Trigger
      {...props}
      data-slot="select-trigger"
      className={cn(
        'flex h-10 w-full items-center justify-between rounded-md border border-charcoal-500 bg-white px-3 py-2 text-base text-charcoal-800 placeholder:text-charcoal-600 md:text-sm',
        'focus:outline-none focus:ring-2 focus:ring-brand-purple focus:ring-offset-2',
        'disabled:cursor-not-allowed disabled:opacity-50',
        '[&>span]:line-clamp-1',
        className,
      )}
    >
      {children}
      <SelectPrimitive.Icon asChild>
        <ChevronDown className="h-4 w-4 opacity-60" />
      </SelectPrimitive.Icon>
    </SelectPrimitive.Trigger>
  );
}

export function SelectScrollUpButton({ className, ...props }: SelectScrollProps): ReactElement {
  return (
    <SelectPrimitive.ScrollUpButton
      {...props}
      data-slot="select-scroll-up-button"
      className={cn('flex cursor-default items-center justify-center py-1', className)}
    >
      <ChevronUp className="h-4 w-4" />
    </SelectPrimitive.ScrollUpButton>
  );
}

export function SelectScrollDownButton({ className, ...props }: SelectScrollProps): ReactElement {
  return (
    <SelectPrimitive.ScrollDownButton
      {...props}
      data-slot="select-scroll-down-button"
      className={cn('flex cursor-default items-center justify-center py-1', className)}
    >
      <ChevronDown className="h-4 w-4" />
    </SelectPrimitive.ScrollDownButton>
  );
}

export function SelectContent({
  className,
  children,
  position = 'popper',
  ...props
}: SelectContentProps): ReactElement {
  return (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Content
        {...props}
        position={position}
        data-slot="select-content"
        className={cn(
          'relative z-50 max-h-96 min-w-[8rem] overflow-hidden rounded-md border border-charcoal-300 bg-white text-charcoal-800 shadow-md',
          'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
          position === 'popper' &&
            'data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1',
          className,
        )}
      >
        <SelectScrollUpButton />
        <SelectPrimitive.Viewport
          className={cn(
            'p-1',
            position === 'popper' &&
              'h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)]',
          )}
        >
          {children}
        </SelectPrimitive.Viewport>
        <SelectScrollDownButton />
      </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
  );
}

export function SelectLabel({ className, ...props }: SelectLabelProps): ReactElement {
  return (
    <SelectPrimitive.Label
      {...props}
      data-slot="select-label"
      className={cn('px-2 py-1.5 text-sm font-semibold', className)}
    />
  );
}

export function SelectItem({ className, children, indicatorSide = 'left', ...props }: SelectItemProps): ReactElement {
  return (
    <SelectPrimitive.Item
      {...props}
      data-slot="select-item"
      className={cn(
        'relative flex w-full cursor-default select-none items-center rounded-sm py-1.5 text-sm outline-none',
        indicatorSide === 'left' ? 'pl-8 pr-2' : 'pl-2 pr-8',
        'focus:bg-charcoal-50 focus:text-charcoal-900',
        'data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
        className,
      )}
    >
      <span
        className={cn(
          'absolute flex h-3.5 w-3.5 items-center justify-center',
          indicatorSide === 'left' ? 'left-2' : 'right-2',
        )}
      >
        <SelectPrimitive.ItemIndicator>
          <Check className="h-4 w-4" />
        </SelectPrimitive.ItemIndicator>
      </span>
      <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
    </SelectPrimitive.Item>
  );
}

export function SelectSeparator({ className, ...props }: SelectSeparatorProps): ReactElement {
  return (
    <SelectPrimitive.Separator
      {...props}
      data-slot="select-separator"
      className={cn('-mx-1 my-1 h-px bg-charcoal-300', className)}
    />
  );
}
